import { z } from 'zod';
import { emailSchema } from './auth';
import { pageFields, pageQuery, timestampSchema } from './common';

export const staffMemberSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  name: z.string().nullable(),
  photoUrl: z.string().nullable(),
  active: z.boolean(),
  admin: z.boolean(),
  alertFailures: z.boolean(),
  alertNotices: z.boolean(),
  you: z.boolean(),
  createdAt: timestampSchema,
  lastLoginAt: timestampSchema.nullable(),
  removedAt: timestampSchema.nullable(),
});

export const staffListSchema = z.object({ staff: z.array(staffMemberSchema) });

export const staffInviteSchema = z.object({ email: emailSchema });

export const staffAdminInputSchema = z.object({ admin: z.boolean() });

export const staffAlertsInputSchema = z
  .object({ failures: z.boolean().optional(), notices: z.boolean().optional() })
  .refine((input) => Object.keys(input).length > 0, 'Change at least one alert');

export const staffInviteResultSchema = z.object({
  member: staffMemberSchema,
  invited: z.boolean(),
});

export const ACTIVITY_AREAS = ['content', 'offer', 'families', 'team'] as const;

export const ACTIVITY_TARGETS = [
  'question',
  'reading_text',
  'topics',
  'availability',
  'product',
  'family',
  'integrations',
  'webshop',
  'staff',
] as const;

export const activityQuerySchema = z.object({
  actor: z.uuid().optional(),
  area: z.enum(ACTIVITY_AREAS).optional(),
  family: z.uuid().optional(),
  from: z.iso.date().optional(),
  to: z.iso.date().optional(),
  ...pageQuery,
});

export const activityEntrySchema = z.object({
  id: z.uuid(),
  at: timestampSchema,
  area: z.enum(ACTIVITY_AREAS),
  summary: z.string(),
  actor: z
    .object({
      id: z.uuid(),
      email: z.string(),
      name: z.string().nullable(),
      photoUrl: z.string().nullable(),
    })
    .nullable(),
  target: z
    .object({
      type: z.enum(ACTIVITY_TARGETS),
      id: z.string().nullable(),
    })
    .nullable(),
});

export const activityListSchema = z.object({
  entries: z.array(activityEntrySchema),
  ...pageFields,
});

export type StaffMember = z.infer<typeof staffMemberSchema>;
export type StaffAdminInput = z.infer<typeof staffAdminInputSchema>;
export type StaffAlertsInput = z.infer<typeof staffAlertsInputSchema>;
export type StaffList = z.infer<typeof staffListSchema>;
export type StaffInviteResult = z.infer<typeof staffInviteResultSchema>;
export type ActivityArea = (typeof ACTIVITY_AREAS)[number];
export type ActivityTarget = (typeof ACTIVITY_TARGETS)[number];
export type ActivityFilters = z.output<typeof activityQuerySchema>;
export type ActivityQuery = Partial<ActivityFilters>;
export type ActivityEntry = z.infer<typeof activityEntrySchema>;
export type ActivityList = z.infer<typeof activityListSchema>;
